import { Link } from 'react-router-dom';
import { BookType } from '../api/types';
import { Badge } from './Badge';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { StatusBadge } from './StatusBadge';
import { formattedDate } from '../../../utils/dates';
import { Spinner } from '../../../components/Spinner';
import { Button } from '../../../components/Button';
import { useUpdateBookStatusMutation } from '../api/booksApi';
import { toast } from 'react-toastify';

type BookRowProps = {
  book: BookType;
  onDeletePress: (book: BookType) => void;
  onEditPress: (book: BookType) => void;
};

export const BookRow = ({ book, onDeletePress, onEditPress }: BookRowProps) => {
  const isProcessing = book.is_processing;
  const isActive = !book.is_draft;
  console.log({ isActive });
  const [updateBookStatus, { isLoading }] = useUpdateBookStatusMutation();

  const onStatusChange = async () => {
    try {
      await updateBookStatus({ bookId: book.id, isDraft: isActive }).unwrap();
      toast.success(`Book ${isActive ? 'drafted' : 'published'} successfully`);
    } catch (err) {
      toast.error('An error occurred');
    }
  };
  return (
    <div className="flex gap-4 flex-col sm:flex-row w-full justify-between items-center bg-white rounded-xl p-4 shadow-sm">
      <Link to={`/books/${book.id}`} className="w-full sm:w-auto">
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <img
            src={book.cover}
            alt={book.title}
            className="w-full sm:w-[11rem] h-[20rem] sm:h-[15rem] object-cover rounded-xl"
          />
          <div className="flex flex-col gap-2 items-center sm:items-start">
            {isProcessing ? (
              <div className="flex flex-col items-center justify-center">
                <Spinner label="Generating fields with AI" />
              </div>
            ) : (
              <>
                <h2 className="font-bold text-xl">{book.title}</h2>
                <p className="text-cyan-500 font-semibold text-lg">{book.author}</p>
                <div className="align-start flex items-center gap-3">
                  <Badge text={book.category} />
                  <StatusBadge isDraft={book.is_draft} />
                </div>
              </>
            )}
            <div className="flex flex-col gap-2 mt-4">
              <p className="text-xs">Created at {formattedDate(new Date(book.created))}</p>
              <p className="text-xs">Last update at {formattedDate(new Date(book.modified))}</p>
            </div>
          </div>
        </div>
      </Link>
      <div className="flex flex-col items-center sm:items-end justify-between gap-6 h-full items-end">
        <p className="font-bold text-green-700 text-xl">€{book.price}</p>
        {!isProcessing && (
          <div className="flex items-center gap-3">
            <button
              className="bg-green-500 p-3 rounded-lg hover:bg-green-400 transition duration-200"
              onClick={() => onEditPress(book)}>
              <PencilIcon className="size-4 text-white" />
            </button>
            <button
              className="bg-red-500 p-3 rounded-lg hover:bg-red-400 transition duration-200"
              onClick={() => onDeletePress(book)}>
              <TrashIcon className="size-4 text-white" />
            </button>
          </div>
        )}
        <Button
          variant={isActive ? 'secondary' : 'primary'}
          onClick={onStatusChange}
          disabled={isLoading}>
          {isActive ? 'Move to draft' : 'Publish'}
        </Button>
      </div>
    </div>
  );
};
